/**
 * Server-side paging cursor for the windowed album wall.
 *
 * The next get_albums offset is the count of server rows consumed, never the
 * deduped in-memory length: dedupe/patch can leave indexAlbums shorter than
 * total forever, which re-fetched the final page in a loop (#11015).
 */
import { diagnosticLog } from '../utils/diagnosticLogger';
import { getGetAlbumsStormTrigger } from './libraryAlbumWallStorm';

export const SAME_PAGE_MAX_PER_MIN = 3;

const WINDOW_MS = 60_000;
const BEACON_COOLDOWN_MS = 60_000;

let nextOffset = 0;
let exhausted = false;
let requestAt = new Map<number, number[]>();
let lastBeaconAt = 0;

export function resetAlbumPageCursor(consumedRows = 0): void {
  nextOffset = Math.max(0, consumedRows);
  exhausted = false;
  requestAt = new Map();
}

export function getAlbumPageCursor(): number {
  return nextOffset;
}

/** Move the cursor past rows the server returned; an empty page means the list ended. */
export function advanceAlbumPageCursor(rowCount: number): void {
  nextOffset += Math.max(0, rowCount);
  if (rowCount <= 0) exhausted = true;
}

/** A row removed upstream of the cursor shifts every later server offset down by one. */
export function shiftAlbumPageCursorForRemoval(): void {
  nextOffset = Math.max(0, nextOffset - 1);
}

export function isAlbumPagingExhausted(total: number): boolean {
  return exhausted || nextOffset >= total;
}

function recentRequests(offset: number, now: number): number[] {
  return (requestAt.get(offset) ?? []).filter((t) => now - t < WINDOW_MS);
}

export function isSamePageRequestBlocked(offset: number): boolean {
  return recentRequests(offset, Date.now()).length >= SAME_PAGE_MAX_PER_MIN;
}

/** Record a get_albums page request; false (plus a beacon) when the same page is over budget. */
export function noteAlbumPageRequest(args: { offset: number; limit: number; total: number }): boolean {
  const now = Date.now();
  const recent = recentRequests(args.offset, now);
  if (recent.length >= SAME_PAGE_MAX_PER_MIN) {
    requestAt.set(args.offset, recent);
    if (now - lastBeaconAt >= BEACON_COOLDOWN_MS) {
      lastBeaconAt = now;
      try {
        diagnosticLog('warn', 'js:render', 'get_albums_same_page_loop', {
          offset: args.offset,
          limit: args.limit,
          total: args.total,
          calls_per_min: recent.length,
          trigger: getGetAlbumsStormTrigger(),
        });
      } catch {
        /* never throw from a beacon */
      }
    }
    return false;
  }
  recent.push(now);
  requestAt.set(args.offset, recent);
  return true;
}
